import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance, API } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, X, Edit2, Download, FileText, Upload, Eye, Calculator, FolderKanban } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';
import PayBillModal from '../components/PayBillModal';
import { errorMessage } from '../utils/errorMessage';

// Helper function to process Arabic text for proper display in PDF
const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

const SupplierBills = ({ user }) => {
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectTarget, setProjectTarget] = useState(null);
  const [projectChoice, setProjectChoice] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Check if user can delete/cancel a bill
  const canDelete = (bill) => {
    if (bill.status === 'cancelled') return false;
    if (user?.role === 'admin') return true;
    return bill.status === 'draft';
  };
  const formFileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [formData, setFormData] = useState({
    supplier_id: '',
    bill_date: new Date().toISOString().split('T')[0],
    due_date: '',
    supplier_bill_no: '',
    purchase_order_id: '',
    project_id: '',
    items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0 }],
    subtotal: 0,
    vat_rate: 15,
    vat_amount: 0,
    total: 0,
    amount_paid: 0,
    notes: '',
    attachment_url: ''
  });

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [billsRes, suppliersRes, posRes, projectsRes] = await Promise.all([
        axiosInstance.get('/supplier-bills'),
        axiosInstance.get('/suppliers'),
        axiosInstance.get('/purchase-orders'),
        axiosInstance.get('/projects')
      ]);
      setBills(billsRes.data);
      setSuppliers(suppliersRes.data);
      setPurchaseOrders(posRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = (supplierId) => {
    setFormData({
      ...formData,
      supplier_id: supplierId,
      purchase_order_id: ''
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    const vatAmount = subtotal * (formData.vat_rate / 100);
    
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      vat_amount: vatAmount,
      total: subtotal + vatAmount
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
      const vatAmount = subtotal * (formData.vat_rate / 100);
      
      setFormData({
        ...formData,
        items: newItems,
        subtotal,
        vat_amount: vatAmount,
        total: subtotal + vatAmount
      });
    }
  };

  const handleVatRateChange = (rate) => {
    const vatAmount = formData.subtotal * (rate / 100);
    setFormData({
      ...formData,
      vat_rate: rate,
      vat_amount: vatAmount,
      total: formData.subtotal + vatAmount
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id || formData.items.length === 0) {
      toast.error('Please select a supplier and add items');
      return;
    }

    try {
      let billId;
      if (editingBill) {
        await axiosInstance.put(`/supplier-bills/${editingBill.id}`, formData);
        billId = editingBill.id;
        toast.success('Bill updated successfully');
      } else {
        const response = await axiosInstance.post('/supplier-bills', formData);
        billId = response.data.id;
        if (response.data.approval_status === 'pending') {
          toast.success('Bill submitted for approval');
        } else {
          toast.success('Bill created successfully');
        }
      }
      
      // Upload attachment if selected
      if (selectedFile && billId) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        try {
          await axiosInstance.post(`/supplier-bills/${billId}/upload-attachment`, uploadFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Attachment uploaded');
        } catch (uploadError) {
          toast.error('Bill created but attachment upload failed');
        }
      }
      
      setShowModal(false);
      resetForm();
      setSelectedFile(null);
      fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to save bill'));
    }
  };

  const handleEdit = (bill) => {
    setEditingBill(bill);
    setFormData({
      supplier_id: bill.supplier_id,
      bill_date: bill.bill_date,
      due_date: bill.due_date,
      supplier_bill_no: bill.supplier_bill_no || '',
      purchase_order_id: bill.purchase_order_id || '',
      project_id: bill.project_id || '',
      items: bill.items,
      subtotal: bill.subtotal,
      vat_rate: bill.vat_rate,
      vat_amount: bill.vat_amount,
      total: bill.total,
      amount_paid: bill.amount_paid,
      notes: bill.notes || ''
    });
    setShowModal(true);
  };

  const handleStatusChange = async (billId, newStatus) => {
    try {
      await axiosInstance.put(`/supplier-bills/${billId}/status?status=${newStatus}`);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedBill || paymentAmount <= 0) return;
    
    try {
      await axiosInstance.put(`/supplier-bills/${selectedBill.id}/payment?amount=${paymentAmount}`);
      toast.success('Payment recorded');
      setShowPaymentModal(false);
      setPaymentAmount(0);
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleUploadAttachment = async (billId, file) => {
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axiosInstance.post(
        `/supplier-bills/${billId}/upload-attachment`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Invoice image uploaded');
      
      // Update selected bill with new attachment
      if (selectedBill && selectedBill.id === billId) {
        setSelectedBill({ ...selectedBill, attachment_url: response.data.attachment_url });
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleCorrectAmount = async (bill) => {
    const val = window.prompt(
      `تصحيح مبلغ الفاتورة ${bill.bill_number}\nأدخل الإجمالي الصحيح الذي تدفعه فعلاً (الحالي: ${bill.total}):`,
      String(bill.total ?? '')
    );
    if (val === null) return;
    const newTotal = parseFloat(val);
    if (!newTotal || newTotal <= 0) return toast.error('أدخل مبلغاً صحيحاً أكبر من صفر');
    const withVat = window.confirm(
      'هل المبلغ شامل الضريبة؟\nموافق = شامل الضريبة (يُستخرج الأساس والضريبة تلقائياً)\nإلغاء = بدون ضريبة (لن يدخل في إقرار الضريبة)'
    );
    try {
      const res = await axiosInstance.post(`/supplier-bills/${bill.id}/correct-amount`, {
        new_total: newTotal,
        vat_mode: withVat ? 'inclusive' : 'none',
      });
      toast.success(res.data?.message_ar || 'تم تصحيح المبلغ');
      fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'فشل تصحيح المبلغ'));
    }
  };

  const openProjectModal = (bill) => {
    setProjectTarget(bill);
    setProjectChoice(bill.project_id || '');
    setShowProjectModal(true);
  };

  const handleLinkProject = async () => {
    if (!projectTarget) return;
    try {
      const res = await axiosInstance.post(`/supplier-bills/${projectTarget.id}/link-project`, {
        project_id: projectChoice || null,
      });
      toast.success(res.data?.message_ar || 'تم الربط بالمشروع');
      setShowProjectModal(false);
      setProjectTarget(null);
      if (selectedBill?.id === projectTarget.id) {
        setSelectedBill({ ...selectedBill, project_id: res.data.project_id, project_name: res.data.project_name });
      }
      fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'فشل ربط الفاتورة بالمشروع'));
    }
  };

  const handleDelete = async (id, bill) => {
    const isDraft = bill?.status === 'draft';
    const reason = window.prompt(
      isDraft
        ? 'حذف المسودة نهائياً؟ اضغط موافق للتأكيد (السبب اختياري)'
        : 'سبب الإلغاء (اختياري) — سيتم إنشاء قيد عكسي وتبقى الفاتورة ظاهرة بحالة "ملغاة"'
    );
    if (reason === null) return;
    try {
      const res = await axiosInstance.delete(`/supplier-bills/${id}`, {
        params: reason ? { reason } : {},
      });
      toast.success(res.data?.message_ar || 'تم إلغاء الفاتورة وإنشاء قيد عكسي');
      fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'فشل إلغاء الفاتورة'));
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      bill_date: new Date().toISOString().split('T')[0],
      due_date: '',
      supplier_bill_no: '',
      purchase_order_id: '',
      project_id: '',
      items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0 }],
      subtotal: 0,
      vat_rate: 15,
      vat_amount: 0,
      total: 0,
      amount_paid: 0,
      notes: '',
      attachment_url: ''
    });
    setEditingBill(null);
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const downloadPDF = async (bill) => {
    const doc = new jsPDF();
    
    // Add Arabic font to jsPDF
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');
    
    // Add Company Logo
    try {
      const logoResponse = await fetch('/logo.png');
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoBase64, 'PNG', 10, 5, 52, 30);
      }
    } catch (err) {
      console.error('Logo load failed:', err);
    }
    
    // Company Header - Arabic Name (right aligned)
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(10, 66, 117);
    doc.text(processArabicText('شركة الطريق العالي المتميز'), 190, 10, { align: 'right' });
    
    // English company name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Distinct High Road', 190, 16, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Hafsa Bint Omar Street, AlRawdah Dist, Riyadh, KSA', 190, 22, { align: 'right' });
    doc.text('VAT: 314477769800003 | CR: 7052942476', 190, 27, { align: 'right' });
    doc.text('Tel: +966552168158 | Email: Info@dhr-sa.com', 190, 32, { align: 'right' });
    
    // Line separator
    doc.setDrawColor(10, 66, 117);
    doc.setLineWidth(0.5);
    doc.line(20, 38, 190, 38);

    // Bill Title (centered)
    doc.setFontSize(18);
    doc.setTextColor(10, 66, 117);
    doc.setFont('helvetica', 'bold');
    doc.text('SUPPLIER BILL', 105, 48, { align: 'center' });
    
    // Arabic title
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText('فاتورة مشتريات'), 105, 55, { align: 'center' });
    doc.setFont('helvetica', 'normal');

    // Two columns: Supplier Details (Left) | Bill Details (Right)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(10, 66, 117);
    doc.text('Supplier Details', 20, 65);
    doc.text('Bill Details', 115, 65);

    // Supplier Details (Left Side)
    doc.setTextColor(0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let supplierY = 73;
    doc.text(`Supplier: ${bill.supplier_name}`, 20, supplierY);
    supplierY += 6;
    
    if (bill.supplier_name_arabic) {
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(bill.supplier_name_arabic), 20, supplierY);
      doc.setFont('helvetica', 'normal');
      supplierY += 6;
    }
    
    if (bill.supplier_vat_number) {
      doc.text(`VAT: ${bill.supplier_vat_number}`, 20, supplierY);
      supplierY += 6;
    }
    
    if (bill.supplier_address) {
      const addressText = bill.supplier_address;
      if (/[\u0600-\u06FF]/.test(addressText)) {
        doc.text('Address: ', 20, supplierY);
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(addressText), 38, supplierY);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.text(`Address: ${addressText}`, 20, supplierY);
      }
    }

    // Bill Details (Right Side)
    doc.setFontSize(9);
    let billY = 73;
    doc.text(`Bill No: ${bill.bill_number}`, 115, billY);
    billY += 6;
    if (bill.supplier_bill_no) {
      doc.text(`Supplier Bill No: ${bill.supplier_bill_no}`, 115, billY);
      billY += 6;
    }
    doc.text(`Bill Date: ${bill.bill_date}`, 115, billY);
    billY += 6;
    doc.text(`Due Date: ${bill.due_date}`, 115, billY);
    billY += 6;
    if (bill.purchase_order_no) {
      doc.text(`PO: ${bill.purchase_order_no}`, 115, billY);
      billY += 6;
    }
    
    // Status
    const statusColors = {
      draft: [100, 100, 100],
      received: [59, 130, 246],
      partial: [245, 158, 11],
      paid: [34, 197, 94],
      cancelled: [239, 68, 68]
    };
    doc.setTextColor(...(statusColors[bill.status] || [100, 100, 100]));
    doc.text(`Status: ${bill.status?.toUpperCase() || 'DRAFT'}`, 115, billY);
    doc.setTextColor(0);

    // Items Table
    const tableData = bill.items.map((item, index) => [
      index + 1,
      item.description,
      item.quantity.toString(),
      item.unit || 'unit',
      `${item.unit_price.toFixed(2)} SAR`,
      `${item.total.toFixed(2)} SAR`
    ]);

    autoTable(doc, {
      startY: 105,
      head: [['#', 'Description', 'Qty', 'Unit', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [10, 66, 117],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 65 },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    });

    // Totals
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 150;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Subtotal: ${bill.subtotal.toFixed(2)} SAR`, 140, finalY);
    doc.text(`VAT (${bill.vat_rate}%): ${bill.vat_amount.toFixed(2)} SAR`, 140, finalY + 6);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(10, 66, 117);
    doc.text(`Total: ${bill.total.toFixed(2)} SAR`, 140, finalY + 14);
    
    // Payment info
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 128, 0);
    doc.text(`Paid: ${(bill.amount_paid || 0).toFixed(2)} SAR`, 140, finalY + 22);
    doc.setTextColor(220, 38, 38);
    doc.text(`Remaining: ${(bill.amount_remaining || 0).toFixed(2)} SAR`, 140, finalY + 28);

    // Notes
    if (bill.notes) {
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('Notes:', 14, finalY + 10);
      const notesText = bill.notes;
      if (/[\u0600-\u06FF]/.test(notesText)) {
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(notesText), 14, finalY + 16);
      } else {
        doc.text(notesText, 14, finalY + 16);
      }
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    doc.text('This is a computer-generated document.', 105, 285, { align: 'center' });

    // Download PDF
    doc.save(`SupplierBill-${bill.bill_number}.pdf`);
    toast.success('PDF downloaded');
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      received: 'bg-blue-100 text-blue-700',
      partial: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Get supplier's POs
  const supplierPOs = purchaseOrders.filter(po => po.supplier_id === formData.supplier_id);

  // Stats — soft-cancelled bills are excluded from all financial totals
  const liveBills = bills.filter(b => b.status !== 'cancelled');
  const stats = {
    total: liveBills.length,
    unpaid: liveBills.filter(b => b.status === 'received' || b.status === 'draft').length,
    partial: liveBills.filter(b => b.status === 'partial').length,
    totalAmount: liveBills.reduce((sum, b) => sum + b.total, 0),
    totalPaid: liveBills.reduce((sum, b) => sum + (b.amount_paid || 0), 0),
    totalRemaining: liveBills.reduce((sum, b) => sum + (b.amount_remaining || 0), 0)
  };

  if (loading) {
    return <div className="text-center py-12">Loading supplier bills...</div>;
  }

  return (
    <div data-testid="supplier-bills-page">
      <div className="page-header">
        <h2 className="page-title">Supplier Bills</h2>
        <button
          data-testid="add-bill-button"
          onClick={openNewModal}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Bill
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon blue">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Bills</h3>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Unpaid</h3>
          <p className="text-3xl font-bold text-yellow-600">{stats.unpaid}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <span className="font-bold text-lg">SAR</span>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Amount</h3>
          <p className="text-2xl font-bold text-red-600">{stats.totalAmount.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <span className="font-bold text-lg">SAR</span>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Remaining</h3>
          <p className="text-2xl font-bold text-orange-600">{stats.totalRemaining.toLocaleString()} SAR</p>
        </div>
      </div>

      {/* Bills Table - Split View */}
      <div className="flex gap-4">
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${selectedBill ? 'w-1/2' : 'w-full'}`}>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Bill No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Remaining</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bills.length > 0 ? (
                bills.map((bill) => (
                  <tr 
                    key={bill.id} 
                    data-testid={`bill-row-${bill.id}`}
                    onClick={() => setSelectedBill(selectedBill?.id === bill.id ? null : bill)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedBill?.id === bill.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''} ${bill.status === 'cancelled' ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-600">{bill.bill_number}</span>
                      {bill.supplier_bill_no && (
                        <p className="text-xs text-gray-500">{bill.supplier_bill_no}</p>
                      )}
                      {bill.project_name && (
                        <p className="text-xs text-indigo-600 font-medium" dir="rtl" data-testid={`bill-project-badge-${bill.id}`}>
                          🏗 {bill.project_name}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{bill.supplier_name}</p>
                        {bill.supplier_name_arabic && (
                          <p className="text-sm text-gray-500" dir="rtl">{bill.supplier_name_arabic}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{bill.bill_date}</td>
                    <td className="px-4 py-3 text-right font-semibold">{bill.total.toLocaleString()} SAR</td>
                    <td className={`px-4 py-3 text-right font-semibold ${bill.status === 'cancelled' ? 'text-gray-400' : bill.amount_remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {bill.status === 'cancelled' ? '—' : `${(bill.amount_remaining || 0).toLocaleString()} SAR`}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.status)}`}>
                        {bill.status === 'cancelled' ? 'ملغاة' : bill.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => downloadPDF(bill)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {bill.status !== 'cancelled' && (
                          <button
                            onClick={() => handleEdit(bill)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                        {bill.status !== 'cancelled' && (
                          <button
                            onClick={() => openProjectModal(bill)}
                            data-testid={`link-project-btn-${bill.id}`}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="ربط بمشروع"
                          >
                            <FolderKanban className="w-4 h-4" />
                          </button>
                        )}
                        {bill.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCorrectAmount(bill)}
                            data-testid={`correct-amount-btn-${bill.id}`}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                            title="تصحيح المبلغ (قيد جديد تلقائياً)"
                          >
                            <Calculator className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete(bill) && (
                          <button
                            onClick={() => handleDelete(bill.id, bill)}
                            data-testid={`cancel-bill-btn-${bill.id}`}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title={bill.status === 'draft' ? 'حذف المسودة' : 'إلغاء (قيد عكسي)'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                    No supplier bills found. Create your first bill!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bill Details Panel */}
        {selectedBill && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedBill.bill_number}</h3>
                <p className="text-sm text-gray-500">Supplier Bill Details</p>
              </div>
              <button 
                onClick={() => setSelectedBill(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status & Actions */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              {selectedBill.project_name && (
                <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-700" dir="rtl" data-testid="bill-details-project">
                  🏗 {selectedBill.project_name}
                </span>
              )}
              <select
                value={selectedBill.status}
                onChange={(e) => {
                  handleStatusChange(selectedBill.id, e.target.value);
                  setSelectedBill({...selectedBill, status: e.target.value});
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border-0 cursor-pointer ${getStatusColor(selectedBill.status)}`}
              >
                <option value="draft">Draft</option>
                <option value="received">Received</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={() => downloadPDF(selectedBill)}
                className="btn-primary text-sm py-1 px-3"
              >
                <Download className="w-4 h-4 inline mr-1" />
                PDF
              </button>
              {selectedBill.amount_remaining > 0 && (
                <button
                  onClick={() => {
                    setPaymentAmount(selectedBill.amount_remaining);
                    setShowPaymentModal(true);
                  }}
                  className="bg-green-600 text-white text-sm py-1 px-3 rounded-lg hover:bg-green-700 flex items-center gap-1"
                >
                  <span className="font-bold text-xs">SAR</span>
                  Pay
                </button>
              )}
            </div>

            {/* Supplier Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Supplier Information</h4>
              <p className="font-medium">{selectedBill.supplier_name}</p>
              {selectedBill.supplier_name_arabic && (
                <p className="text-gray-600" dir="rtl">{selectedBill.supplier_name_arabic}</p>
              )}
              {selectedBill.supplier_vat_number && (
                <p className="text-sm text-gray-500">VAT: {selectedBill.supplier_vat_number}</p>
              )}
            </div>

            {/* Bill Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Bill Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Bill Date:</span>
                  <p className="font-medium">{selectedBill.bill_date}</p>
                </div>
                <div>
                  <span className="text-gray-500">Due Date:</span>
                  <p className="font-medium">{selectedBill.due_date}</p>
                </div>
                {selectedBill.supplier_bill_no && (
                  <div>
                    <span className="text-gray-500">Supplier Bill No:</span>
                    <p className="font-medium">{selectedBill.supplier_bill_no}</p>
                  </div>
                )}
                {selectedBill.purchase_order_no && (
                  <div>
                    <span className="text-gray-500">Purchase Order:</span>
                    <p className="font-medium text-blue-600">{selectedBill.purchase_order_no}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Items</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-center">Unit</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBill.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 text-center">{item.quantity}</td>
                        <td className="px-3 py-2 text-center">{item.unit || 'unit'}</td>
                        <td className="px-3 py-2 text-right">{item.unit_price?.toLocaleString()} SAR</td>
                        <td className="px-3 py-2 text-right font-medium">{item.total?.toLocaleString()} SAR</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Subtotal:</span>
                <span>{selectedBill.subtotal?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>VAT ({selectedBill.vat_rate || 15}%):</span>
                <span>{selectedBill.vat_amount?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                <span>Total:</span>
                <span className="text-blue-600">{selectedBill.total?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm text-green-600 mt-2">
                <span>Paid:</span>
                <span>{(selectedBill.amount_paid || 0).toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm text-red-600">
                <span>Remaining:</span>
                <span>{(selectedBill.amount_remaining || 0).toLocaleString()} SAR</span>
              </div>
            </div>

            {/* Notes */}
            {selectedBill.notes && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                <p className="text-sm text-gray-600">{selectedBill.notes}</p>
              </div>
            )}

            {/* Invoice Attachment */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-700">Invoice File</h4>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*,.pdf,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleUploadAttachment(selectedBill.id, e.target.files[0]);
                    }
                  }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload File'}
                </button>
              </div>
              
              {selectedBill.attachment_url ? (
                <div className="relative">
                  {selectedBill.attachment_url.toLowerCase().endsWith('.pdf') ? (
                    <div className="border rounded-lg p-4 bg-red-50">
                      <div className="flex items-center gap-3">
                        <FileText className="w-12 h-12 text-red-600" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">PDF Document</p>
                          <p className="text-sm text-gray-500">Invoice attachment</p>
                        </div>
                        <a
                          href={`${API.replace('/api', '')}${selectedBill.attachment_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                        >
                          <Eye className="w-4 h-4" /> View PDF
                        </a>
                      </div>
                    </div>
                  ) : (
                    <>
                      <img 
                        src={`${API.replace('/api', '')}${selectedBill.attachment_url}`}
                        alt="Invoice attachment"
                        className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setShowImageModal(true)}
                      />
                      <button
                        onClick={() => setShowImageModal(true)}
                        className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" /> View Full
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500 text-sm">No invoice file attached</p>
                  <p className="text-gray-400 text-xs mt-1">Upload Image or PDF</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && selectedBill?.attachment_url && !selectedBill.attachment_url.toLowerCase().endsWith('.pdf') && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={() => setShowImageModal(false)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={`${API.replace('/api', '')}${selectedBill.attachment_url}`}
              alt="Invoice attachment full"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Link-Project Modal */}
      {showProjectModal && projectTarget && (
        <div className="modal-overlay" onClick={() => setShowProjectModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title" dir="rtl">ربط {projectTarget.bill_number} بمشروع</h3>
              <button onClick={() => setShowProjectModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4" dir="rtl">
              {projectTarget.project_name && (
                <p className="text-sm text-gray-600">
                  المشروع الحالي: <span className="font-semibold text-indigo-600">{projectTarget.project_name}</span>
                </p>
              )}
              <div className="form-group">
                <label className="form-label">اختر المشروع</label>
                <select
                  data-testid="link-project-select"
                  value={projectChoice}
                  onChange={(e) => setProjectChoice(e.target.value)}
                  className="form-input"
                >
                  <option value="">بدون مشروع (فك الربط)</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                سيتم تحديث وسم المشروع على القيود المرحَّلة تلقائياً (المبالغ لا تتغير).
                وإذا كانت التكلفة على حساب مصروف عمومي سيُنشأ قيد إعادة تبويب إلى «تكلفة المشاريع»
                لتظهر في ربحية المشروع.
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowProjectModal(false)} className="btn-secondary px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                  إلغاء
                </button>
                <button
                  data-testid="link-project-confirm-btn"
                  onClick={handleLinkProject}
                  className="btn-primary px-4 py-2"
                >
                  ربط بالمشروع
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingBill ? `Edit ${editingBill.bill_number}` : 'New Supplier Bill'}
              </h3>
              <button onClick={() => setShowModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Supplier & Dates */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Supplier *</label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => handleSupplierChange(e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name_english}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Bill Date *</label>
                  <input
                    type="date"
                    value={formData.bill_date}
                    onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date *</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Supplier Bill No</label>
                  <input
                    type="text"
                    value={formData.supplier_bill_no}
                    onChange={(e) => setFormData({ ...formData, supplier_bill_no: e.target.value })}
                    className="form-input"
                    placeholder="Original bill number"
                  />
                </div>
              </div>

              {/* Purchase Order */}
              {supplierPOs.length > 0 && (
                <div className="form-group mb-4">
                  <label className="form-label">Link to Purchase Order</label>
                  <select
                    value={formData.purchase_order_id}
                    onChange={(e) => setFormData({ ...formData, purchase_order_id: e.target.value })}
                    className="form-input"
                  >
                    <option value="">None</option>
                    {supplierPOs.map(po => (
                      <option key={po.id} value={po.id}>{po.po_number} - {po.total.toLocaleString()} SAR</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Project linkage */}
              <div className="form-group mb-4">
                <label className="form-label">المشروع / Project</label>
                <select
                  data-testid="bill-form-project-select"
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="form-input"
                >
                  <option value="">بدون مشروع — None</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Items */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="form-label">Items</label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + Add Item
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 font-medium mb-1">
                    <div className="col-span-3">Description</div>
                    <div className="col-span-2 text-center">Qty</div>
                    <div className="col-span-2 text-center">Unit</div>
                    <div className="col-span-2 text-center">Price</div>
                    <div className="col-span-2 text-center">Total</div>
                    <div className="col-span-1"></div>
                  </div>
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-3">
                        <input
                          type="text"
                          placeholder="Item description"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          className="form-input text-sm"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          placeholder="1"
                          value={item.quantity === 0 ? '' : item.quantity}
                          onChange={(e) => {
                            const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            handleItemChange(index, 'quantity', val);
                          }}
                          className="form-input text-sm text-center text-lg font-semibold"
                          min="0"
                          step="any"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <select
                          value={item.unit || 'unit'}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          className="form-input text-sm"
                        >
                          <option value="unit">Unit</option>
                          <option value="pcs">Pcs</option>
                          <option value="kg">Kg</option>
                          <option value="m">Meter</option>
                          <option value="m2">Sq.M</option>
                          <option value="m3">Cu.M</option>
                          <option value="liter">Liter</option>
                          <option value="box">Box</option>
                          <option value="set">Set</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          placeholder="0.00"
                          value={item.unit_price === 0 ? '' : item.unit_price}
                          onChange={(e) => {
                            const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            handleItemChange(index, 'unit_price', val);
                          }}
                          className="form-input text-sm text-right"
                          min="0"
                          step="any"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="text"
                          value={`${(item.total || 0).toFixed(2)} SAR`}
                          className="form-input text-sm bg-gray-100 text-right"
                          readOnly
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attachment Upload */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="flex items-center justify-between mb-2">
                  <label className="form-label flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    Invoice Attachment (PDF/Image)
                  </label>
                  <input
                    type="file"
                    ref={formFileInputRef}
                    accept="image/*,.pdf,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setSelectedFile(e.target.files[0]);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => formFileInputRef.current?.click()}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Upload className="w-4 h-4" />
                    Choose File
                  </button>
                </div>
                {selectedFile ? (
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-2">
                    No file selected. You can attach a PDF or image of the supplier invoice.
                  </p>
                )}
              </div>

              {/* Totals */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="form-input"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{formData.subtotal.toFixed(2)} SAR</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">VAT:</span>
                      <div className="flex items-center gap-2">
                        <select
                          value={formData.vat_rate}
                          onChange={(e) => handleVatRateChange(parseFloat(e.target.value))}
                          className="form-input w-20 text-sm py-1"
                        >
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="15">15%</option>
                        </select>
                        <span className="font-medium">{formData.vat_amount.toFixed(2)} SAR</span>
                      </div>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-bold text-gray-900">Total:</span>
                      <span className="font-bold text-blue-600 text-lg">{formData.total.toFixed(2)} SAR</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingBill ? 'Update Bill' : 'Create Bill'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedBill && (
        <PayBillModal
          bill={selectedBill}
          onClose={() => setShowPaymentModal(false)}
          onPaid={() => {
            setShowPaymentModal(false);
            // Refresh the bills list (fetchBills is the standard hook in this file).
            if (typeof fetchData === 'function') fetchData();
          }}
        />
      )}
    </div>
  );
};

export default SupplierBills;

